import { io, Socket } from 'socket.io-client';

// Derive socket URL from the API base URL (strip /api/v1 path)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.cricclash.com';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_BASE;

type Listener = () => void;

class SocketService {
  private socket: Socket | null = null;
  private listeners = new Set<Listener>();
  private version = 0;

  /** React subscription hook's external store interface. */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Return a monotonically-increasing snapshot that changes whenever the
   * socket identity or connection state changes. Used by useSyncExternalStore
   * so components re-render reactively.
   */
  getSnapshot = (): number => this.version;

  private bump() {
    this.version++;
    this.listeners.forEach((l) => l());
  }

  /**
   * Global connection — auto-joins user:{userId} room server-side.
   *
   * Accepts either a static token (for back-compat) or a provider function
   * that returns the latest token on demand. The provider form is strongly
   * preferred: socket.io reuses whatever handshake auth was supplied on
   * every reconnect, so a stale access token from the initial connect will
   * keep failing the server's JWT check long after the axios interceptor
   * has refreshed it. The provider is invoked by socket.io on each (re)connect.
   */
  connect(tokenOrProvider: string | (() => string | null)): Socket {
    if (this.socket?.connected) return this.socket;

    console.log('[Socket] Connecting to', SOCKET_URL);

    const getToken = typeof tokenOrProvider === 'function'
      ? tokenOrProvider
      : () => tokenOrProvider;

    this.socket = io(SOCKET_URL, {
      // Function form: socket.io calls this on every connection attempt,
      // including reconnects — so a token refresh elsewhere is picked up.
      auth: (cb: (data: { token: string }) => void) => {
        const token = getToken() || '';
        cb({ token });
      },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      console.log('[Socket] Connected:', this.socket?.id);
      this.bump();
    });

    this.socket.on('connect_error', (err) => {
      console.error('[Socket] Connection error:', err.message);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      this.bump();
    });

    // Initial bump so subscribers with null state transition to having a socket.
    this.bump();
    return this.socket;
  }

  /** Join a contest room (for auction/contest-specific events) */
  joinContest(contestId: string): void {
    console.log('[Socket] join:contest →', contestId);
    this.socket?.emit('join:contest', contestId);
  }

  /** Leave a contest room */
  leaveContest(contestId: string): void {
    this.socket?.emit('leave:contest', contestId);
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.bump();
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

const socketService = new SocketService();
export default socketService;
